
import { defineComponent, ref } from "vue";

import getQrCodeService, { TrackingEvents } from "@/services/QrCodeService";

import Loader from "@/components/Loader.vue";
import Message from "@/components/Message.vue";
import ProgressBar from "@/components/ProgressBar.vue";
import RGPDPopin from "@/components/RGPDPopin.vue";
import { mapState } from "vuex";
import { State } from "@/store";
import { sendLog } from "@/utils/utils";
import { ProjectAuthType } from "@/typings/AuthType";

export default defineComponent({
    name: "Upload",
    components: {
        Loader,
        Message,
        ProgressBar,
        RGPDPopin
    },
    data() {
        return {
            messageOverlay: {},
            displayMessage: false,
            uploadProgression: 0,
            transcodeProgression: 0,
            transcodeProgressTimer: null as number | null,
            transcodeDuration: 5000,
            transcodeStep: 2,
            rgpdPopinDisplayed: false,
            startUploading: false,
            allowRetry: false
        };
    },
    computed: {
        isUploading() {
            return this.uploadProgression < 100;
        },
        canStartUpload() {
            return this.termsAndConditionsConsent === true && !this.authenticated;
        },
        ...mapState({
            qrcodeObject: (state: State) => state.qrcode.qrcodeObject,
            videoUrl: (state: State) => state.qrcode.videoUrl,
            recordedVideoFile: (state: State) => state.record.videoFile,
            recordedVideoName: (state: State) => state.record.videoName,
            userData: (state: State) => ({
                firstName: state.record.userFirstName,
                lastName: state.record.userLastName,
                email: state.record.userEmail,
                phone: state.record.userPhone,
                videoAgreement: state.record.userVideoAgreement
            }),
            authenticated: (state: State) => state.auth.authenticated,
            askEmailConsent: (state: State) =>
                state.auth.authenticated && state.qrcode.qrcodeObject.project.authTypeId !== ProjectAuthType.NONE
        })
    },
    watch: {
        qrcodeObject() {
            if (this.qrcodeObject.experiences) {
                this.onServerError(true);
            } else {
                this.$router.replace("success");
            }
        },
        canStartUpload(newValue) {
            if (newValue) {
                this.startUpload();
            } else {
                this.displayRGPDPopin();
            }
        }
    },
    async mounted() {
        this.$store.dispatch("auth/check");
        const termsAndConditionsConsent = ref(false);
        const project = this.qrcodeObject.project;
        const isMultiVideo = project.typeId === 2;
        if (!this.authenticated && project.authTypeId === ProjectAuthType.REQUIRED) {
            this.$router.replace("SignIn");
        }
        if (isMultiVideo && this.qrcodeObject.project.shortlinkBurnFields){
            this.qrcodeObject.project.shortlinkBurnFields.forEach(field => {
                if (field.isRequired){
                    if(field.fieldId === 1 && !this.$store.state.record.userFirstName || 
                        field.fieldId === 2 && !this.$store.state.record.userLastName ||
                        field.fieldId === 3 && !this.$store.state.record.userEmail ||
                        field.fieldId === 4 && !this.$store.state.record.userPhone) {
                        this.$router.replace("user-data");
                    }
                }
            });
        }

        if (!this.recordedVideoFile) {
            this.$router.replace("/");
            return;
        }

        try {
            if (!this.canStartUpload) {
                this.displayRGPDPopin();
            } else {
                this.startUpload();
            }
        } catch (e) {
            this.displayRGPDPopin();
        }
    },
    beforeUnmount() {
        if (this.transcodeProgressTimer) {
            clearInterval(this.transcodeProgressTimer);
        }
    },
    methods: {
        displayRGPDPopin() {
            this.rgpdPopinDisplayed = true;
        },

        async onRGPDClosed() {
            this.rgpdPopinDisplayed = false;
            this.termsAndConditionsConsent = true;
            this.startUpload();
        },

        async startUpload() {
            // to avoid any duplicate upload
            if (this.startUploading) {
                return;
            }

            this.startUploading = true;
            this.rgpdPopinDisplayed = false;
            const { userFirstName, userLastName, userEmail, userPhone } = this.$store.state.record;
            const burningFields = [];
            if (userFirstName) {
                burningFields.push({
                    "field_id": 1,
                    "value": userFirstName
                });
            }
            if (userLastName) {
                burningFields.push({
                    "field_id": 2,
                    "value": userLastName
                });
            }
            if (userEmail) {
                burningFields.push({
                    "field_id": 3,
                    "value": userEmail
                });
            }
            if (userPhone) {
                burningFields.push({
                    "field_id": 4,
                    "value": userPhone
                });
            }
            const presignedFile = await getQrCodeService().presignFile(this.recordedVideoName);
            if (!presignedFile) {
                this.allowRetry = true;
                this.$router.replace("/");
                this.onServerError(false, "Failed to get presigned URL");
                return;
            };
            const { pathToSave, presignedUrl } = presignedFile.data[0];

            const awsFile = await getQrCodeService().sendToS3(this.recordedVideoFile, presignedUrl, this.recordedVideoFile.type)         
            if (!awsFile) {
                this.allowRetry = true;
                this.$router.replace("/");
                this.onServerError(false, "Failed to get aws file");
                return;
            }
            const isauthRequired = this.qrcodeObject.project.authTypeId !== ProjectAuthType.NONE;
            const savedFile = await getQrCodeService().saveExperience(
                this.qrcodeObject.shortlink.publicCryptoId,
                pathToSave,
                this.qrcodeObject.project.burningVideoMaxLength,
                JSON.stringify(burningFields),
                this.qrcodeObject.isBurnt,
                isauthRequired,
            );
            if (!savedFile) {
                this.allowRetry = true;
                this.$router.replace("/");
                this.onServerError(false, "Failed when saving experience");
                return;
            }

            this.$store.dispatch(
                "qrcode/load",
                this.qrcodeObject.shortlink.publicCryptoId
            );
            sendLog(TrackingEvents.BURN, this.qrcodeObject,savedFile.data.id.toString(),);
            this.allowRetry = false;
        },
        
        onUploadProgress(progression: number) {
            this.uploadProgression = progression;

            if (progression >= 100) {
                this.transcodeProgressTimer = setInterval(
                    this.updateTranscodeProgression,
                    (this.transcodeDuration * this.transcodeStep) / 100
                );
            }
        },
        showMessage(_isError, title, text) {
            this.messageOverlay = {
                isError: _isError,
                title: title,
                text: text
            };
            this.displayMessage = true;
        },
        onServerError(isError = true, e = null) {
            this.showMessage(
                isError,
                this.$t("general.serverError", e),
                this.$t("upload.uploadError")
            );
        },
        onMessageClosed() {
            this.displayMessage = false;
            this.$router.replace("record");
        },
        updateTranscodeProgression() {
            this.transcodeProgression = Math.min(
                this.transcodeProgression + this.transcodeStep,
                100
            );

            if (this.transcodeProgression === 100) {
                clearInterval(this.transcodeProgressTimer);
            }
        },
        resetTranscodeProgression() {
            clearInterval(this.transcodeProgressTimer);
            this.uploadProgression = 0;
            this.transcodeProgression = 0;
        },
        retryUpload() {
            this.resetTranscodeProgression();
            this.startUploading = false;
            this.startUpload();
        },
        retryHandler() {
            this.displayMessage = false;
            this.retryUpload();
        }
    }
});
